import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";

import { BarChart3, ArrowUp, ChevronDown, ChevronRight, Download } from "lucide-react";
import MiniGridLoader from '@/components/MiniGridLoader';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useDataExport } from "@/hooks/useDataExport";
import { hapticSoft } from "@/utils/haptics";
import { useNavigate } from "react-router-dom";

interface ChatMessage {
  role: "assistant" | "user";
  content: string;
  toolsUsed?: string[] | null;
  codeExecuted?: string | null;
  action?: "export_data";
  actionHandled?: boolean;
}

// Map tool names to user-friendly descriptions
const toolDescriptions: Record<string, string> = {
  get_detailed_entries: "📋 Retrieved entry logs",
  get_metric_values: "📊 Fetched metric data",
  get_user_metrics: "📈 Loaded your metrics",
  set_metric_goal: "🎯 Updated goal",
  execute_analysis: "🔢 Ran analysis script",
};

// Collapsible component for viewing executed code
const CodeCollapsible = ({ code }: { code: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="mt-2 pt-2 border-t border-border/50">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 text-xs text-muted-foreground/70 hover:text-muted-foreground transition-colors"
      >
        <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        View code
      </button>
      {isOpen && (
        <pre className="mt-2 p-2 bg-background/50 rounded text-xs overflow-x-auto max-h-[200px] overflow-y-auto">
          <code className="text-muted-foreground">{code}</code>
        </pre>
      )}
    </div>
  );
};

interface DataInsightsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  initialQuestion?: string;
  userCredits?: number;
  isPremium?: boolean;
  onRedirectToViewCreator?: (prompt: string) => void;
}

export const DataInsightsDialog = ({
  open,
  onOpenChange,
  userId,
  initialQuestion = "",
  userCredits,
  isPremium,
  onRedirectToViewCreator,
}: DataInsightsDialogProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasInitialQuestion, setHasInitialQuestion] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea (grows upward since it's at the bottom of the dialog)
  const autoResizeInput = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, []);
  const { exportData, isExporting } = useDataExport(userId);

  // Auto-submit if there's an initial question
  useEffect(() => {
    if (open && messages.length === 0) {
      // If there's an initial question, submit it automatically
      if (initialQuestion && !hasInitialQuestion) {
        setHasInitialQuestion(true);
        setTimeout(() => {
          submitMessage(initialQuestion);
        }, 500);
      }
    }
  }, [open, initialQuestion]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setMessages([]);
      setInputValue("");
      setIsLoading(false);
      setHasInitialQuestion(false);
    }
  }, [open]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const submitMessage = async (messageText: string) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = messageText.trim();
    setInputValue("");
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error(t('notifications.toast.please_sign_in', 'Please sign in to continue'));
        setIsLoading(false);
        return;
      }

      // Get the conversation history for context
      const conversationHistory = messages.map(m => ({
        role: m.role,
        content: m.content,
      }));

      const response = await supabase.functions.invoke("data-insights", {
        body: {
          userMessage,
          conversationHistory,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });

      if (response.error) {
        if (response.error.message?.includes("insufficient_credits")) {
          toast.error("You're out of credits");
          setIsLoading(false);
          return;
        }
        throw new Error(response.error.message || "Failed to get response");
      }

      // Check for redirect to custom view creator
      if (response.data.redirectToViewCreator) {
        setIsLoading(false);
        onOpenChange(false);
        onRedirectToViewCreator?.(response.data.originalRequest || userMessage);
        return;
      }

      // Check for data export request
      if (response.data.exportData) {
        setIsLoading(false);
        setMessages(prev => [...prev, {
          role: "assistant",
          content: response.data.message || "Would you like to export all your tracking data?",
          action: "export_data",
        }]);
        hapticSoft();
        return;
      }

      const { message, creditsRemaining, toolsUsed, codeExecuted } = response.data;

      // Update credits display
      queryClient.invalidateQueries({ queryKey: ["user-credits", userId] });

      setMessages(prev => [...prev, {
        role: "assistant",
        content: message,
        toolsUsed: toolsUsed || null,
        codeExecuted: codeExecuted || null,
      }]);
      hapticSoft();
    } catch (error: any) {
      console.error("[DataInsightsDialog] Error sending message:", error);
      toast.error(t('notifications.toast.failed_to_send_message', 'Failed to send message'));
      // Remove the user message if the request failed
      setMessages(prev => prev.slice(0, -1));
      setInputValue(messageText);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    await submitMessage(inputValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full h-full max-w-full max-h-full rounded-none border-0 flex flex-col" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-primary" />
            {t('data_insights.title', 'Data Insights')}
          </DialogTitle>
        </DialogHeader>

        {/* Chat Messages Area */}
        <div className="flex-1 overflow-y-auto min-h-0 space-y-4 py-4">
          <AnimatePresence initial={false}>
            {messages.map((message, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className={`flex flex-col ${message.role === "user" ? "items-end" : "items-start"}`}
              >
                {/* Show tools used for assistant messages */}
                {message.role === "assistant" && message.toolsUsed && message.toolsUsed.length > 0 && (
                  <div className="text-xs text-muted-foreground mb-1 px-1 space-y-2">
                    {message.toolsUsed.map((tool, i) => (
                      <div key={i} className="italic">{toolDescriptions[tool] || "⚙️ Processing..."}</div>
                    ))}
                  </div>
                )}
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                    message.role === "user"
                      ? "bg-blue-500 text-white"
                      : "bg-muted"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  {/* Export data action buttons */}
                  {message.role === "assistant" && message.action === "export_data" && !message.actionHandled && (
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        variant="default"
                        disabled={isExporting}
                        className="rounded-full gap-1.5"
                        onClick={async () => {
                          // Mark as handled so buttons disappear
                          setMessages(prev => prev.map((m, i) =>
                            i === index ? { ...m, actionHandled: true } : m
                          ));
                          await exportData();
                          setMessages(prev => [...prev, {
                            role: "assistant",
                            content: "Your data export has been downloaded! 📦",
                          }]);
                        }}
                      >
                        <Download className="h-3.5 w-3.5" />
                        {isExporting ? "Exporting..." : "Yes, export"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full"
                        onClick={() => {
                          setMessages(prev => prev.map((m, i) =>
                            i === index ? { ...m, actionHandled: true } : m
                          ));
                          setMessages(prev => [...prev, {
                            role: "assistant",
                            content: "No problem! Let me know if you need anything else.",
                          }]);
                        }}
                      >
                        No thanks
                      </Button>
                    </div>
                  )}
                  {/* View code collapsible */}
                  {message.role === "assistant" && message.codeExecuted && (
                    <CodeCollapsible code={message.codeExecuted} />
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          
          {/* Loading indicator for AI response */}
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-start"
            >
              <div className="bg-muted rounded-2xl px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <MiniGridLoader className="h-4 w-4" />
                  <span className="text-sm text-muted-foreground">{t('data_insights.analyzing', 'Analyzing your data...')}</span>
                </div>
              </div>
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="flex-shrink-0 pt-2">
          <div className="flex gap-2">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                autoResizeInput();
              }}
              onKeyDown={handleKeyDown}
              placeholder={t('data_insights.input_placeholder', 'Ask AI about your data...')}
              disabled={isLoading}
              rows={1}
              className="flex-1 rounded-2xl flex w-full border border-input bg-background px-3 py-2 text-base md:text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none overflow-hidden"
            />
            <Button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              size="icon"
              className={`h-10 w-10 rounded-full transition-all duration-200 ${
                inputValue.trim() && !isLoading
                  ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-md hover:shadow-lg active:scale-95'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {isLoading ? (
                <MiniGridLoader className="h-4 w-4" />
              ) : (
                <ArrowUp className="h-5 w-5" />
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            {t('data_insights.credit_cost', '0.5 credits per message')}
          </p>
          <button
            onClick={() => navigate(isPremium ? "/credits-success" : "/upgrade")}
            className="text-xs text-muted-foreground/60 text-center flex items-center justify-center gap-0.5 mx-auto hover:text-muted-foreground transition-colors cursor-pointer"
          >
            You have {typeof userCredits === 'number' ? (userCredits / 10).toFixed(1) : '...'} credits
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
